import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';

import { useEthereum } from 'src/hooks/useEthereum';
import { shortAddress } from 'src/utils/shortAddress';
import { useResponsive } from 'src/hooks/useResponsive';
import { WALLET_CONFIG_ERROR } from 'src/config/wagmiConfig';
import DisconnectConfirmDialog from 'src/components/wallet-connect/DisconnectConfirmDialog';
import OpenMetaMaskDialog from 'src/components/wallet-connect/OpenMetaMaskDialog';
import {
  cancelMetaMaskPrompt,
  clearPendingMetaMaskLink,
  setMetaMaskCancelHandler,
  useIsMetaMaskPromptHost,
  useMetaMaskConnectActive,
  useMetaMaskOpenLink,
} from 'src/utils/metaMaskOpenLink';
import MetaMaskRequiredDialog from 'src/components/MetaMask-dialog/MetaMaskRequiredDialog';
import { AuthContext } from 'src/auth/context';

const METAMASK_CONNECT_TIMEOUT_MS = 15_000;
// Inside the MetaMask app a connect may wait up to 30s for an earlier request
// to be approved (see useEthereum), so allow longer before giving up.
const IN_APP_CONNECT_TIMEOUT_MS = 45_000;
// Phone browser: the user switches to the MetaMask app, unlocks it, approves
// and switches back. 15s was often not enough, so a slow approval was shown
// as a failure. "Cancel" in the prompt ends a wait early.
const MOBILE_WEB_CONNECT_TIMEOUT_MS = 120_000;

function getConnectTimeoutMs(isMetaMaskInApp, isMobileWeb) {
  if (isMetaMaskInApp) return IN_APP_CONNECT_TIMEOUT_MS;
  if (isMobileWeb) return MOBILE_WEB_CONNECT_TIMEOUT_MS;
  return METAMASK_CONNECT_TIMEOUT_MS;
}
const METAMASK_TIMEOUT_MARKER = 'METAMASK_CONNECT_TIMEOUT';
const METAMASK_CANCEL_MARKER = 'METAMASK_CONNECT_CANCELLED';
const MOBILE_WEB_TIMEOUT_MESSAGE =
  'MetaMask did not respond. Press Connect again, then approve in MetaMask.';
const IN_APP_TIMEOUT_MESSAGE =
  'MetaMask did not respond. Pull down to refresh the page in MetaMask, then press Connect again.';
const PENDING_REQUEST_MESSAGE =
  'A MetaMask connection request is already open. Finish or cancel it in MetaMask.';

const GREEN_GRADIENT = 'linear-gradient(90deg, #E9E637 0%, #A6DC2E 48%, #6FCF2C 100%)';

function getWalletErrorMessage(message) {
  if (!message) return '';

  const text = String(message).toLowerCase();

  if (text.includes('user rejected')) {
    return 'Connection request was cancelled.';
  }

  if (text.includes('connector not found')) {
    return 'Wallet connector was not found.';
  }

  if (text.includes('no wallet connector available')) {
    return 'No wallet connector is available on this device.';
  }

  if (text.includes('provider not found')) {
    return 'Wallet provider was not found. Open MetaMask and try again.';
  }

  if (text.includes('already pending') || text.includes('request already pending')) {
    return 'A wallet request is already open. Check MetaMask.';
  }

  if (text.includes('reconnecting')) {
    return 'Wallet is reconnecting. Please wait.';
  }

  if (text.includes('localhost') || text.includes('127.0.0.1')) {
    return 'This phone cannot connect to localhost. Use your computer LAN IP instead.';
  }

  return message;
}

// Loose match only — exact wagmi/MetaMask SDK wording varies ("provider not
// found" vs "provider was not found", "connector not found" vs "connector is
// not available", etc). Used to decide whether the dialog's inline hint
// applies, not to gate anything before the fact.
function isNoWalletError(message) {
  if (!message) return false;
  if (message === METAMASK_TIMEOUT_MARKER) return true;

  const text = String(message).toLowerCase();
  const mentionsNotFound = text.includes('not found') || text.includes('not available');

  if (text.includes('provider') && mentionsNotFound) return true;
  if (text.includes('connector') && mentionsNotFound) return true;

  return false;
}

// On mobile, a deep link into an uninstalled app goes nowhere — the SDK's
// connect promise can be left hanging with nothing to reject it. Racing it
// against a timer means an unanswered attempt still resolves the UI instead
// of leaving the button stuck on "Connecting…" forever.
//
// The timer is now cleared on settle. Previously it was never cleared, so every
// successful connect still left a 15s timer that fired into a dead promise.
//
// "Cancel" in the Open MetaMask prompt ends the wait right away.
function connectWithTimeout(connectFn, ms) {
  let timer = null;

  const stop = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(METAMASK_TIMEOUT_MARKER));
    }, ms);

    setMetaMaskCancelHandler(() => {
      reject(new Error(METAMASK_CANCEL_MARKER));
    });
  });

  return Promise.race([connectFn(), stop]).finally(() => {
    if (timer) clearTimeout(timer);
    setMetaMaskCancelHandler(null);
  });
}

// Opens this same page inside MetaMask's own browser, where the wallet is
// built in and no app switching or relay is needed.
function getOpenInMetaMaskLink() {
  if (typeof window === 'undefined') return '';

  const { host, pathname, search } = window.location;

  return `https://metamask.app.link/dapp/${host}${pathname}${search}`;
}

function getTimeoutMessage(isMetaMaskInApp, isMobileWeb) {
  if (isMetaMaskInApp) return IN_APP_TIMEOUT_MESSAGE;
  if (isMobileWeb) return MOBILE_WEB_TIMEOUT_MESSAGE;
  return '';
}

export default function WalletConnectButtonClient({ green = false, fullWidth = false }) {
  const [isActing, setIsActing] = useState(false);
  const [localError, setLocalError] = useState('');
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [metaMaskDialogOpen, setMetaMaskDialogOpen] = useState(false);
  const lastToastMessageRef = useRef('');

  const metaMaskLink = useMetaMaskOpenLink();
  const isPromptHost = useIsMetaMaskPromptHost();
  const connectActive = useMetaMaskConnectActive();

  const { logout, user } = useContext(AuthContext);
  const isMobile = useResponsive('down', 'sm');
  const theme = useTheme();

  const {
    connected,
    isConnecting,
    isDisconnecting,
    isSwitchingNetwork,
    isReconnecting,
    isBusy,
    walletReady,
    connectWallet,
    cancelConnect,
    isMetaMaskInApp,
    isMobileWeb,
    disconnectWallet,
    reconnectWalletFresh,
    accountMismatch,
    connectError,
    switchChainError,
    address,
    walletChainId,
    targetChainId,
    switchNetworkError,
  } = useEthereum();

  // Read inside async callbacks, where the captured `connected` is stale.
  const connectedRef = useRef(connected);
  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);

  // Just connected: the connect prompt is no longer needed.
  useEffect(() => {
    if (!connected) return;
    clearPendingMetaMaskLink();
  }, [connected]);

  const wrongNetwork = useMemo(() => {
    if (!connected) return false;
    if (walletChainId && targetChainId && Number(walletChainId) !== Number(targetChainId)) {
      return true;
    }
    if (switchNetworkError) return true;
    return false;
  }, [connected, walletChainId, targetChainId, switchNetworkError]);

  const busy = useMemo(
    () =>
      isActing || isBusy || isConnecting || isDisconnecting || isSwitchingNetwork || isReconnecting,
    [isActing, isBusy, isConnecting, isDisconnecting, isSwitchingNetwork, isReconnecting]
  );

  const visualState = useMemo(() => {
    if (isReconnecting) return 'reconnecting';
    if (isConnecting || isActing) return 'connecting';
    if (isSwitchingNetwork) return 'switching';
    if (isDisconnecting) return 'disconnecting';
    if (connected && wrongNetwork) return 'wrong-network';
    // A CAIP-25 permission held for a different account than the selected one
    // makes writes hang silently. Surface it instead of letting the user reach
    // the deposit path.
    if (connected && accountMismatch) return 'account-mismatch';
    if (connected && address && !walletReady) return 'preparing';
    if (connected && address) return 'connected';
    return 'disconnected';
  }, [
    isReconnecting,
    isConnecting,
    isActing,
    isSwitchingNetwork,
    isDisconnecting,
    connected,
    wrongNetwork,
    accountMismatch,
    address,
    walletReady,
  ]);

  const displayedError = useMemo(() => {
    if (localError) return localError;
    if (connectError?.message) return connectError.message;
    if (switchChainError?.message) return switchChainError.message;
    return '';
  }, [localError, connectError, switchChainError]);

  const toastMessage = useMemo(() => getWalletErrorMessage(displayedError), [displayedError]);

  useEffect(() => {
    if (!toastMessage) return;
    if (connected && !wrongNetwork) return;
    if (isNoWalletError(displayedError)) return; // dialog's inline hint handles this instead of a toast
    if (toastMessage === lastToastMessageRef.current) return;

    lastToastMessageRef.current = toastMessage;
    toast.error(toastMessage, { id: 'wallet-connect-error', duration: 3500 });
  }, [toastMessage, displayedError, connected, wrongNetwork]);

  useEffect(() => {
    if (!connected) return;
    if (wrongNetwork) return;

    setLocalError('');
    lastToastMessageRef.current = '';
    toast.dismiss('wallet-connect-error');
  }, [connected, wrongNetwork]);

  // ── Connect (dialog button, or direct inside the MetaMask app) ──────────────
  const handleConnectFromDialog = useCallback(async () => {
    if (isActing) return;

    setLocalError('');
    lastToastMessageRef.current = '';
    setIsActing(true);

    try {
      if (isReconnecting) {
        setLocalError('Wallet is reconnecting. Please wait.');
        setMetaMaskDialogOpen(false);
        return;
      }

      if (wrongNetwork) {
        toast.dismiss('wallet-connect-error');
        setMetaMaskDialogOpen(false);
        return;
      }

      const result = await connectWithTimeout(
        connectWallet,
        getConnectTimeoutMs(isMetaMaskInApp, isMobileWeb)
      );

      if (result !== null) {
        toast.dismiss('wallet-connect-error');
        setMetaMaskDialogOpen(false);
        return;
      }

      // connectWallet returns null when already connected or when an earlier
      // request is still open in MetaMask. Never leave the user with no feedback.
      setMetaMaskDialogOpen(false);

      if (!connectedRef.current) {
        setLocalError(PENDING_REQUEST_MESSAGE);
      }
    } catch (err) {
      const message = err?.message || 'Wallet action failed';

      // The timeout does not cancel the underlying connect — MetaMask may have
      // completed after we gave up. Reconcile against live state before
      // reporting a failure the user can see is wrong.
      if (message === METAMASK_TIMEOUT_MARKER && connectedRef.current) {
        toast.dismiss('wallet-connect-error');
        setMetaMaskDialogOpen(false);
        return;
      }

      if (message === METAMASK_CANCEL_MARKER) {
        if (!connectedRef.current) {
          await cancelConnect();
        }
        setMetaMaskDialogOpen(false);
        return;
      }

      // An unanswered attempt stays "pending" in wagmi and blocks every later
      // Connect. Clear it so the next press starts a fresh request.
      if (message === METAMASK_TIMEOUT_MARKER) {
        await cancelConnect();
      }

      const timeoutMessage = getTimeoutMessage(isMetaMaskInApp, isMobileWeb);

      if (message === METAMASK_TIMEOUT_MARKER && timeoutMessage) {
        setLocalError(timeoutMessage);
        setMetaMaskDialogOpen(false);
        return;
      }

      if (isNoWalletError(message)) {
        // Keep the dialog open — its inline hint explains this, and the
        // install button is right there.
        setLocalError(message);
      } else {
        setLocalError(message);
        setMetaMaskDialogOpen(false);
      }
    } finally {
      setIsActing(false);
    }
  }, [
    isActing,
    isReconnecting,
    wrongNetwork,
    connectWallet,
    cancelConnect,
    isMetaMaskInApp,
    isMobileWeb,
  ]);

  // ── Primary button click ──────────────────────────────────────────────────
  const handlePrimaryAction = useCallback(async () => {
    if (busy) return;

    if (WALLET_CONFIG_ERROR) {
      toast.error('Wallet is unavailable — app is misconfigured.', {
        id: 'wallet-connect-error',
      });
      return;
    }

    // Mismatch: the fix is a full session reset, not another plain connect.
    if (connected && accountMismatch) {
      setIsActing(true);

      try {
        await reconnectWalletFresh();
        toast.dismiss('wallet-connect-error');
      } catch (err) {
        setLocalError(err?.message || 'Wallet reset failed');
      } finally {
        setIsActing(false);
      }

      return;
    }

    if (connected && !wrongNetwork) {
      setDisconnectDialogOpen(true);
      return;
    }

    // Inside the MetaMask app the wallet is right here: connect directly
    // instead of showing the "open / install MetaMask" dialog.
    // Everywhere else (desktop and phone browsers) the single "Connect with
    // MetaMask" dialog is used. On phones it also shows "Open MetaMask" when
    // the browser did not switch to the app by itself.
    if (isMetaMaskInApp) {
      await handleConnectFromDialog();
      return;
    }

    setLocalError('');
    setMetaMaskDialogOpen(true);
  }, [
    busy,
    connected,
    wrongNetwork,
    accountMismatch,
    reconnectWalletFresh,
    isMetaMaskInApp,
    handleConnectFromDialog,
  ]);

  const dialogHint = useMemo(() => {
    if (!metaMaskDialogOpen) return '';
    if (!isNoWalletError(localError)) return '';

    if (localError === METAMASK_TIMEOUT_MARKER) {
      return "Still waiting on MetaMask. If it didn't open, install it below and try Connect again.";
    }

    return 'MetaMask was not found. Install it below, then press Connect again.';
  }, [metaMaskDialogOpen, localError]);

  // ── Confirm disconnect (called from dialog) ───────────────────────────────
  const handleConfirmDisconnect = useCallback(async () => {
    setLocalError('');
    lastToastMessageRef.current = '';
    setIsActing(true);

    try {
      await disconnectWallet();
      toast.dismiss('wallet-connect-error');
    } catch (err) {
      setLocalError(err?.message || 'Wallet action failed');
    } finally {
      // Only end the backend session when the WALLET is what authenticates it.
      //
      // This used to run unconditionally. A PHONE-session user who connected a
      // wallet just to deposit was logged out of their phone session the moment
      // they disconnected the wallet — the session and the wallet are separate
      // identities and only a wallet session dies with the wallet.
      if (user?.sessionAuthType === 'wallet') {
        try {
          await logout();
        } catch (logoutErr) {
          console.error('[WalletConnect] logout after disconnect failed', logoutErr);
        }
      }

      setIsActing(false);
      setDisconnectDialogOpen(false);
    }
  }, [disconnectWallet, logout, user]);

  const handleCloseDisconnectDialog = useCallback(() => {
    if (isDisconnecting) return; // prevent closing mid-flight
    setDisconnectDialogOpen(false);
  }, [isDisconnecting]);

  const handleCloseMetaMaskDialog = useCallback(() => {
    if (isActing) {
      // Phone browser: closing while waiting for MetaMask cancels the attempt
      // (the user may never come back from the app). Desktop keeps the old
      // behaviour: the dialog stays until the extension answers.
      if (isMobileWeb) {
        cancelMetaMaskPrompt();
      }
      return;
    }

    setMetaMaskDialogOpen(false);
  }, [isActing, isMobileWeb]);

  // The "Open MetaMask" link belongs in the connect dialog only while this
  // button is connecting.
  const connectOpenLink = useMemo(() => {
    if (!isActing || !metaMaskDialogOpen || !metaMaskLink) return '';
    return metaMaskLink;
  }, [isActing, metaMaskDialogOpen, metaMaskLink]);

  const openInAppLink = useMemo(() => {
    if (!isMobileWeb || !metaMaskDialogOpen) return '';
    return getOpenInMetaMaskLink();
  }, [isMobileWeb, metaMaskDialogOpen]);

  // ── Display helpers ───────────────────────────────────────────────────────
  const buttonLabel = useMemo(() => {
    switch (visualState) {
      case 'reconnecting':
        return 'Reconnecting…';
      case 'connecting':
        return 'Connecting…';
      case 'switching':
        return 'Switching…';
      case 'disconnecting':
        return 'Disconnecting…';
      case 'wrong-network':
        return 'Wrong network';
      case 'account-mismatch':
        return 'Reconnect wallet';
      case 'preparing':
        return 'Preparing…';
      case 'connected':
        return shortAddress(address);
      default:
        return 'Connect';
    }
  }, [visualState, address]);

  const baseBorderColor = useMemo(() => alpha(theme.palette.text.primary, 0.16), [theme]);
  const baseHoverBorderColor = useMemo(() => alpha(theme.palette.text.primary, 0.3), [theme]);
  const baseBackgroundColor = useMemo(() => {
    if (theme.palette.mode === 'dark') {
      return alpha(theme.palette.background.paper, 0.42);
    }

    return alpha(theme.palette.background.paper, 0.78);
  }, [theme]);
  const baseHoverBackgroundColor = useMemo(() => {
    if (theme.palette.mode === 'dark') {
      return alpha(theme.palette.action.hover, 0.42);
    }

    return alpha(theme.palette.action.hover, 0.72);
  }, [theme]);
  const baseTextColor = useMemo(() => theme.palette.text.primary, [theme]);

  const statusDotColor = useMemo(() => {
    if (visualState === 'wrong-network') return theme.palette.error.main;
    if (visualState === 'account-mismatch') return theme.palette.error.main;
    if (visualState === 'preparing') return theme.palette.warning.main;
    if (visualState === 'connected') return theme.palette.success.main;
    return alpha(theme.palette.text.primary, 0.44);
  }, [visualState, theme]);

  const statusDotShadow = useMemo(() => {
    if (visualState === 'wrong-network') {
      return `0 0 0 3px ${alpha(theme.palette.error.main, 0.14)}`;
    }
    if (visualState === 'account-mismatch') {
      return `0 0 0 3px ${alpha(theme.palette.error.main, 0.14)}`;
    }
    if (visualState === 'preparing') {
      return `0 0 0 3px ${alpha(theme.palette.warning.main, 0.14)}`;
    }
    if (visualState === 'connected') {
      return `0 0 0 3px ${alpha(theme.palette.success.main, 0.14)}`;
    }
    return 'none';
  }, [visualState, theme]);

  const fixedButtonWidth = useMemo(() => {
    if (isMobile) return 120;
    return 150;
  }, [isMobile]);

  // ── Variant styling (green vs default) — additive, non-breaking ───────────
  const variantSx = useMemo(() => {
    if (green) {
      return {
        border: 'none',
        color: '#0A0B08',
        background: GREEN_GRADIENT,
        '&:hover': {
          background: GREEN_GRADIENT,
          filter: 'brightness(1.05)',
          border: 'none',
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          background: 'rgba(255,255,255,0.07)',
          color: '#6E7565',
          border: 'none',
          WebkitTextFillColor: '#6E7565',
        },
      };
    }

    return {
      borderColor: baseBorderColor,
      color: baseTextColor,
      backgroundColor: baseBackgroundColor,
      '&:hover': {
        borderColor: baseHoverBorderColor,
        backgroundColor: baseHoverBackgroundColor,
        boxShadow: 'none',
      },
      '&.Mui-disabled': {
        borderColor: baseBorderColor,
        color: alpha(theme.palette.text.primary, 0.68),
        backgroundColor: baseBackgroundColor,
        WebkitTextFillColor: alpha(theme.palette.text.primary, 0.68),
      },
    };
  }, [
    green,
    baseBorderColor,
    baseHoverBorderColor,
    baseBackgroundColor,
    baseHoverBackgroundColor,
    baseTextColor,
    theme,
  ]);

  const widthSx = useMemo(() => {
    if (fullWidth) {
      return { width: '100%' };
    }
    return { minWidth: fixedButtonWidth, maxWidth: fixedButtonWidth };
  }, [fullWidth, fixedButtonWidth]);

  const containerSx = useMemo(() => {
    if (fullWidth) {
      return { width: '100%' };
    }
    return {};
  }, [fullWidth]);

  const containerAlign = useMemo(() => {
    if (fullWidth) return 'stretch';
    return 'flex-start';
  }, [fullWidth]);

  const buttonStateIcon = useMemo(() => {
    if (busy) return <CircularProgress size={14} color="inherit" />;

    if (connected) {
      return (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: statusDotColor,
            boxShadow: statusDotShadow,
            transition: theme.transitions.create(['background-color', 'box-shadow'], {
              duration: 160,
            }),
          }}
        />
      );
    }

    return <Icon icon="solar:wallet-bold" width={16} />;
  }, [busy, connected, statusDotColor, statusDotShadow, theme]);

  const buttonContent = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ minWidth: 0, width: '100%', justifyContent: 'center' }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          color: 'inherit',
        }}
      >
        {buttonStateIcon}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 800,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          lineHeight: 1.2,
        }}
      >
        {buttonLabel}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Stack spacing={1} alignItems={containerAlign} sx={containerSx}>
        <Button
          variant="outlined"
          size="small"
          onClick={handlePrimaryAction}
          disabled={busy || Boolean(WALLET_CONFIG_ERROR)}
          sx={{
            height: 42,
            ...widthSx,
            borderRadius: green ? 2 : 999,
            textTransform: 'none',
            fontWeight: 800,
            justifyContent: 'center',
            boxShadow: 'none',
            transition: theme.transitions.create(['border-color', 'background-color'], {
              duration: 120,
            }),
            ...variantSx,
          }}
        >
          {buttonContent}
        </Button>
      </Stack>

      <DisconnectConfirmDialog
        open={disconnectDialogOpen}
        onClose={handleCloseDisconnectDialog}
        onConfirm={handleConfirmDisconnect}
        address={address}
        isDisconnecting={isDisconnecting || isActing}
      />

      {/* Later approvals only (Send, Switch network). Never during a connect,
          which uses the dialog below, so there is only ever one dialog. */}
      <OpenMetaMaskDialog
        open={isPromptHost && Boolean(metaMaskLink) && !connectActive}
        link={metaMaskLink}
        onCancel={cancelMetaMaskPrompt}
      />

      <MetaMaskRequiredDialog
        open={metaMaskDialogOpen}
        onClose={handleCloseMetaMaskDialog}
        onConnect={handleConnectFromDialog}
        isConnecting={isActing}
        isMobile={isMobile}
        hint={dialogHint}
        openLink={connectOpenLink}
        openInAppLink={openInAppLink}
      />
    </>
  );
}

WalletConnectButtonClient.propTypes = {
  green: PropTypes.bool,
  fullWidth: PropTypes.bool,
};
